import axios from 'axios';
import { apiUrl, requestOperation, unwrapData } from '../../../api/client';
import { refreshAccessToken, refreshCsrfToken, rememberCsrfToken } from '../../../api/client/http';
import { isCsrfFailure } from '../../../lib/authErrors';
import type { AuthUser } from '../../../store/authStore';

export type LoginRequest = {
  email: string;
  password: string;
  mfaCode?: string;
};

type AuthEnvelope<T> = {
  data: T;
};

type LoginData = {
  accessToken: string;
  csrfToken?: string;
  mustChangePassword: boolean;
  user: AuthUser;
};

export type RefreshSessionSummary = {
  sessionId: string;
  device?: string;
  browser?: string;
  os?: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  absoluteExpiresAt: string;
  isCurrentSession: boolean;
};

function normalizeCurrentUser(rawUser: any): AuthUser {
  return {
    id: rawUser.id ?? rawUser._id,
    email: rawUser.email,
    role: rawUser.role,
    profile: rawUser.profile,
    preferences: rawUser.preferences ?? {
      aiProvider: 'openai',
      theme: 'light',
      notificationChannels: [],
      emailDigest: true,
    },
    mfaEnabled: Boolean(rawUser.mfaEnabled),
    mustChangePassword: Boolean(rawUser.mustChangePassword),
    organizationId: typeof rawUser.organizationId === 'object'
      ? rawUser.organizationId?._id ?? rawUser.organizationId?.id
      : rawUser.organizationId,
    organizationBranding: rawUser.organizationBranding ?? (typeof rawUser.organizationId === 'object'
      ? rawUser.organizationId?.branding
      : undefined),
  };
}

export async function login(payload: LoginRequest): Promise<LoginData> {
  const response = await requestOperation<AuthEnvelope<LoginData>>('POST /api/v1/auth/login', {
    body: payload,
    config: { skipAuthRefresh: true },
  });
  const data = unwrapData(response);
  rememberCsrfToken(data.csrfToken);
  return { ...data, user: normalizeCurrentUser(data.user) };
}

export async function getCurrentUser(accessToken?: string): Promise<AuthUser> {
  const { data } = await axios.get<AuthEnvelope<AuthUser>>(apiUrl('/api/v1/auth/me'), {
    withCredentials: true,
    params: { _authProbe: Date.now() },
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });
  return normalizeCurrentUser(data.data);
}

export async function refreshServerSession(): Promise<{ accessToken: string; user: AuthUser }> {
  const accessToken = await refreshAccessToken();
  const user = await getCurrentUser(accessToken);
  return { accessToken, user };
}

export async function logout(): Promise<void> {
  const requestLogout = () => requestOperation('POST /api/v1/auth/logout', {
    config: { skipAuthRefresh: true },
  });

  try {
    await requestLogout();
  } catch (error) {
    if (!isCsrfFailure(error)) throw error;

    // A page may retain an older CSRF value than the API cookie. Recover the
    // matching pair once, then retry the intentional logout request.
    await refreshCsrfToken();
    await requestLogout();
  }
}

export async function logoutAllSessions(): Promise<void> {
  await requestOperation('POST /api/v1/auth/logout-all');
}

export async function logoutOtherSessions(): Promise<void> {
  await requestOperation('POST /api/v1/auth/logout-other-sessions');
}

export async function listRefreshSessions(): Promise<RefreshSessionSummary[]> {
  const response = await requestOperation<AuthEnvelope<RefreshSessionSummary[]>>('GET /api/v1/auth/sessions');
  return unwrapData(response);
}

export async function revokeRefreshSession(sessionId: string): Promise<void> {
  await requestOperation('POST /api/v1/auth/sessions/{sessionId}/revoke', {
    path: { sessionId },
  });
}

export async function changePassword(payload: { currentPassword: string; newPassword: string }) {
  return requestOperation<AuthEnvelope<{ message: string }>>('POST /api/v1/auth/change-password', {
    body: payload,
    config: { skipAuthRefresh: true },
  });
}

export async function setupMfa() {
  return requestOperation<AuthEnvelope<{ secret: string; qrCode: string }>>('POST /api/v1/auth/mfa/setup', {
    config: { skipAuthRefresh: true },
  });
}

export async function enableMfa(token: string) {
  return requestOperation<AuthEnvelope<{ enabled: true; backupCodes: string[] }>>('POST /api/v1/auth/mfa/enable', {
    body: { token },
    config: { skipAuthRefresh: true },
  });
}
